import { useEffect, useState, type ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import { Alert, Paper, Tab, Tabs } from "@mui/material";
import { isMarketReportApiConfigured } from "@/services/marketReportApi/marketReportApi";
import "@/assets/css/Text-General.css";
import "@/assets/css/tabs.css";
import "@/assets/css/marketReports.css";
import { ReportsTab } from "./ReportsTab";
import { MarketTab } from "./MarketTab";
import { AskTab } from "./AskTab";
import { InfoTab } from "./InfoTab";

const TAB_CONFIG = [
  { id: "info", label: "Info" },
  { id: "reports", label: "Reports" },
  { id: "market", label: "Market" },
  { id: "chat", label: "Chat" },
] as const;

type TabId = (typeof TAB_CONFIG)[number]["id"];

function tabIndexFromId(id: TabId | null): number {
  const index = TAB_CONFIG.findIndex((tab) => tab.id === id);
  return index >= 0 ? index : 0;
}

function TabPanel(props: {
  children: ReactNode;
  value: number;
  index: number;
}) {
  const { children, value, index } = props;
  if (value !== index) return null;
  return (
    <div className="market-reports-tab-content">{children}</div>
  );
}

const MarketReports = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab") as TabId | null;
  const [activeTab, setActiveTab] = useState(() => tabIndexFromId(tabParam));

  useEffect(() => {
    setActiveTab(tabIndexFromId(tabParam));
  }, [tabParam]);

  const handleTabChange = (_: unknown, next: number) => {
    setActiveTab(next);
    const tabId = TAB_CONFIG[next]?.id ?? "info";
    if (tabId === "info") {
      setSearchParams({});
    } else {
      setSearchParams({ tab: tabId });
    }
  };

  return (
    <div className="market-reports-page">
      <Paper elevation={0} className="market-reports-card">
        {!isMarketReportApiConfigured() ? (
          <Alert severity="warning" sx={{ m: 2 }}>
            Market Report API is not configured. Set VITE_MARKET_REPORT_API_URL
            and VITE_MARKET_REPORT_API_KEY in your environment.
          </Alert>
        ) : null}

        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="standard"
          TabIndicatorProps={{ className: "tf-tabs__indicator" }}
          className="summary-tabs"
        >
          {TAB_CONFIG.map((tab) => (
            <Tab key={tab.id} className="tf-tab" label={tab.label} />
          ))}
        </Tabs>

        <div className="market-reports-tab-panel">
          <TabPanel value={activeTab} index={0}>
            <InfoTab />
          </TabPanel>
          <TabPanel value={activeTab} index={1}>
            <ReportsTab />
          </TabPanel>
          <TabPanel value={activeTab} index={2}>
            <MarketTab />
          </TabPanel>
          <TabPanel value={activeTab} index={3}>
            <AskTab />
          </TabPanel>
        </div>
      </Paper>
    </div>
  );
};

export default MarketReports;
